import { useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { resetStore } from 'app/store'
import api from '../lib/axios/api'
import {
  getExpiresIn,
  getLastActivityAt,
  getTokenUpdatedAt,
  LAST_ACTIVITY_KEY,
  releaseTokenRefreshLock,
  setLastActivityAt,
  touchLastActivityAt,
  tryAcquireTokenRefreshLock
} from '../lib/auth/tokenStorage'
import useAuthToken from './useAuthToken'

const TIMEOUT_CONFIG = {
  REFRESH_BUFFER: 120, // Refresh 2 minutes before token expiry; skip refresh 2 minutes before idle logout
  FALLBACK_EXPIRES_IN: 1800, // Used when login/refresh did not persist expires_in
  CHECK_INTERVAL: 1000 // 1 second - How often the inactivity/refresh check runs
}

// Idle logout and token refresh both follow the persisted token TTL.
const getTokenTtlSeconds = () => {
  const expiresIn = getExpiresIn()
  return Number.isFinite(expiresIn) && expiresIn > 0
    ? expiresIn
    : TIMEOUT_CONFIG.FALLBACK_EXPIRES_IN
}

const getInactivitySeconds = () => getTokenTtlSeconds()

const getIdleRefreshCutoffSeconds = () =>
  Math.max(getInactivitySeconds() - TIMEOUT_CONFIG.REFRESH_BUFFER, 1)

const getTokenRefreshAgeSeconds = () =>
  Math.max(getTokenTtlSeconds() - TIMEOUT_CONFIG.REFRESH_BUFFER, 1)

const USER_ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click'
]

export const useInactivityTimeout = onBeforeLogout => {
  const navigate = useNavigate()
  const { clearTokens, setTokens, isAuthenticated } = useAuthToken()

  const lastActivityTime = useRef(Date.now())
  const lastTokenRefreshTime = useRef(Date.now())
  const isRefreshing = useRef(false)

  const intervalRef = useRef(null)

  const isLoggingOut = useRef(false)

  const handleLogout = useCallback(async () => {
    if (isLoggingOut.current) {
      return
    }

    isLoggingOut.current = true
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (typeof onBeforeLogout === 'function') {
      try {
        await onBeforeLogout()
      } catch (error) {
        console.error('Error in onBeforeLogout callback:', error)
      }
    }

    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('auth_access_customers_filter_applied')
    }

    clearTokens()
    resetStore()
    setTimeout(() => {
      navigate('/login', { replace: true })
    }, 100)
  }, [onBeforeLogout, clearTokens, navigate])

  const refreshUserToken = useCallback(async () => {
    if (isRefreshing.current) {
      return
    }

    const storedUpdatedAt = getTokenUpdatedAt()
    if (
      storedUpdatedAt &&
      (Date.now() - storedUpdatedAt) / 1000 < getTokenRefreshAgeSeconds()
    ) {
      lastTokenRefreshTime.current = storedUpdatedAt
      return
    }

    if (!tryAcquireTokenRefreshLock()) {
      return
    }

    isRefreshing.current = true
    try {
      const data = await api.requestTokenRefresh()

      if (data?.access_token) {
        setTokens(data)
        lastTokenRefreshTime.current = Date.now()
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      handleLogout()
    } finally {
      isRefreshing.current = false
      releaseTokenRefreshLock()
    }
  }, [setTokens, handleLogout])

  const handleLogoutRef = useRef(handleLogout)
  const refreshUserTokenRef = useRef(refreshUserToken)

  useEffect(() => {
    handleLogoutRef.current = handleLogout
    refreshUserTokenRef.current = refreshUserToken
  }, [handleLogout, refreshUserToken])

  useEffect(() => {
    const updateActivity = () => {
      const now = Date.now()
      lastActivityTime.current = now
      touchLastActivityAt()
    }

    const onStorage = event => {
      if (event.key !== LAST_ACTIVITY_KEY || !event.newValue) {
        return
      }

      const sharedActivity = Number.parseInt(event.newValue, 10)
      if (
        !Number.isNaN(sharedActivity) &&
        sharedActivity > lastActivityTime.current
      ) {
        lastActivityTime.current = sharedActivity
      }
    }

    USER_ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, updateActivity, { passive: true })
    )
    window.addEventListener('storage', onStorage)

    return () => {
      USER_ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, updateActivity)
      )
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      isLoggingOut.current = false
      return
    }

    lastActivityTime.current = Date.now()
    setLastActivityAt(lastActivityTime.current)
    lastTokenRefreshTime.current = getTokenUpdatedAt() ?? Date.now()

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      const now = Date.now()
      const sharedActivity = getLastActivityAt() ?? 0
      if (sharedActivity > lastActivityTime.current) {
        lastActivityTime.current = sharedActivity
      }

      const inactivityDuration = (now - lastActivityTime.current) / 1000

      if (inactivityDuration >= getInactivitySeconds()) {
        if (!isLoggingOut.current) {
          handleLogoutRef.current()
        }
        return
      }

      const storedTokenUpdatedAt = getTokenUpdatedAt()
      if (
        storedTokenUpdatedAt &&
        storedTokenUpdatedAt > lastTokenRefreshTime.current
      ) {
        lastTokenRefreshTime.current = storedTokenUpdatedAt
      }

      const tokenAge = (now - lastTokenRefreshTime.current) / 1000
      const refreshAge = getTokenRefreshAgeSeconds()
      if (
        inactivityDuration < getIdleRefreshCutoffSeconds() &&
        tokenAge >= refreshAge &&
        !isRefreshing.current
      ) {
        refreshUserTokenRef.current()
      }
    }, TIMEOUT_CONFIG.CHECK_INTERVAL)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isAuthenticated])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  return null
}
